import { Controller } from '@hotwired/stimulus'

import { Chart } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ChoroplethController, GeoFeature, ProjectionScale, ColorScale } from 'chartjs-chart-geo'
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix'
import { mergeDeepLeft } from 'ramda'

import { buildCallsReceivablesAndPayablesCombo } from '../charts/calls_receivables_and_payables_combo'
import { buildHourByDayDensity } from '../charts/hour_by_day_density'
import { buildInboundRequestsAndDuplicatesCombo } from '../charts/inbound_requests_and_duplicates_combo'
import { applyGlobalChartDefaults } from '../charts/theme'
import { EllipsizeTicksPlugin } from '../charts/ellipsize_ticks_plugin'
import { TotalsAxisPlugin } from '../charts/totals_axis_plugin'
import { chartPalette, choroplethInterpolate, barCapBorderRadius } from '../charts/palette'
import { formatNumber } from '../app/modules/utilities'

Chart.register(ChoroplethController, GeoFeature, ProjectionScale, ColorScale)
Chart.register(MatrixController, MatrixElement)

// NOTE: ----------------------------------------------------------------------------
// The Safe wrapper below is because the Turbo frame will refresh the <script>
// but the chart <canvas> is not destroyed with it. This can cause desync issues
// with how the legend is drawn, especially combined with resizing.
//
// I do not like this fragile solution and there may be a better way to do this
// by tearing down the canvas inside the turbo frame.
/// ----------------------------------------------------------------------------
// Skip drawing until the labels have been prepared, to avoid Turbo frame
// teardown/rebuild issues.
const SafeChartDataLabels = {
    ...ChartDataLabels,
    afterDatasetsDraw(chart, args, options) {
        if (!chart['$datalabels']?._labels) return
        return ChartDataLabels.afterDatasetsDraw(chart, args, options)
    },
}

// Renderer registry: a presenter can return a `renderer` key (alongside the usual
// type/data) to delegate canvas construction to a bespoke build module instead of the
// default JSON-merge path. Charts with no `renderer` key are unaffected.
const CUSTOM_RENDERERS = {
    calls_receivables_and_payables_combo: buildCallsReceivablesAndPayablesCombo,
    hour_by_day_density: buildHourByDayDensity,
    inbound_requests_and_duplicates_combo: buildInboundRequestsAndDuplicatesCombo,
}

export default class extends Controller {
    static targets = [
        'canvas',
        'options',
        'chartContainer',
        'toolbar',
        'legend',
        'comboModeButton',
        'comboSwitch',
    ]

    // Header toggles drive the combo renderer client-side. State is NOT persisted; it lives
    // on the controller (which is on the .card, so it survives turbo-frame data reloads of
    // the body). Defaults mirror the initial header markup (Counts active, Receivables % off).
    comboState = { mode: 'counts', showReceivables: false }

    // Hour × Day Density measure ('vol' | 'rcv'), same idea: lives on the .card controller so
    // it survives turbo-frame data reloads (the header toggle is outside the reloading frame).
    // Defaults to the .active button in the markup (Volume).
    densityMeasure = 'vol'

    optionsTargetConnected() {
        this.chart ? this.refreshChart() : this.initChart()
    }

    initChart() {
        // Apply the global Chart.js theme once, lazily: by the first chart's connect every
        // Chart.js component is registered (charts_js_action_controller imports chart.js/auto),
        // so the tooltip/plugin defaults this themes actually exist. Idempotent (see theme.js).
        applyGlobalChartDefaults()

        // A presenter can register a custom renderer (e.g. the calls combo). If one handles
        // this chart, keep only the resize wiring the default path sets up.
        if (this.#buildComboChart()) {
            this._resizeChartHandler = () => this.resizeChart()
            window.addEventListener('resize', this._resizeChartHandler)
            return
        }

        this.optionsFromParam.plugins = []

        const opts = mergeDeepLeft(this.optionsFromParam, {
            options: this.defaultRetreaverChartOptions,
            plugins: [],
        })

        if (this.isEmpty) {
            opts.data = this.emptyData
        }

        if (this.enabledPlugins.includes('datalabels')) {
            opts.plugins.push(SafeChartDataLabels) // Adding js
        }

        // Horizontal charts ellipsize their row labels to the live axis width.
        // The plugin self-gates on indexAxis, so every chart can carry it.
        opts.plugins.push(EllipsizeTicksPlugin)

        // Ranked breakdown bars render a row-totals column as a right-edge axis;
        // the plugin owns the y2 scale and refreshes it on every chart update.
        if (this.optionsFromParam.shouldShowTotals) {
            opts.plugins.push(TotalsAxisPlugin)
        }

        // Render the legend as HTML in the card footer instead of on the canvas.
        opts.plugins.push(this.htmlLegendPlugin)

        this.chart = new Chart(this.canvasTarget, opts)
        this.#setChartContainerHeightByLabelHeight()
        this.#applyRankedBreakdownHorizontalMinWidth()

        // Resize chart on window resize
        this.resizeChart()
        this._resizeChartHandler = () => this.resizeChart()
        window.addEventListener('resize', this._resizeChartHandler)
    }

    disconnect() {
        window.removeEventListener('resize', this._resizeChartHandler)
        this.#destroyChart()

        if (this.hasChartContainerTarget) {
            this.chartContainerTarget.style.removeProperty('min-width')
        }
    }

    // Chart.js debounces resize→update through a setTimeout it never clears on destroy(). When a
    // Turbo morph (e.g. switching chart templates) removes this card, its container resizes on the
    // way out and queues that update; ~resizeDelay later the orphaned timer fires on the now-
    // destroyed chart, whose canvas is null, and Chart.js calls getComputedStyle(null) — the
    // "Cannot read properties of null (reading 'ownerDocument')" crash. The timer reads
    // chart.update dynamically, so stubbing it before destroy makes that late callback a no-op.
    #destroyChart() {
        if (!this.chart) return
        this.chart.update = () => {}
        this.chart.destroy()
        this.chart = null
    }

    refreshChart() {
        // Custom-renderer charts (e.g. the combo) bind gradients/axes to the live canvas,
        // so a data reload destroys and rebuilds rather than patching the dataset arrays.
        if (this.#buildComboChart()) return

        this.chart.data = this.optionsFromParam.data

        if (this.isEmpty) {
            this.chart.data = this.emptyData
        }

        this.chart.update('none')
        this.#setChartContainerHeightByLabelHeight()
        this.#applyRankedBreakdownHorizontalMinWidth()
        this.resizeChart()
    }

    resizeChart() {
        if (!this.hasChartContainerTarget || !this.hasToolbarTarget) return

        // During a Turbo teardown the container is detached from the document while the
        // chart is briefly still alive. Writing maxHeight below would nudge Chart.js'
        // ResizeObserver into resizing a parent-less canvas and crash. Bail out instead.
        // (Note: this runs from the htmlLegend afterUpdate hook during `new Chart(...)`,
        // before this.chart is assigned, so we can't guard on this.chart here.)
        if (!this.chartContainerTarget.isConnected) return

        const parentCard = this.chartContainerTarget.closest('.card')
        if (!parentCard) return
        const parentCardBody = parentCard.querySelector('.card-body')
        if (!parentCardBody) return

        // Find card and toolbar height
        const cardHeight = parentCard.clientHeight
        const toolbarHeight = this.toolbarTarget.clientHeight

        // The HTML legend lives in the card footer, which also eats vertical space.
        const footer = parentCard.querySelector('.card-footer')
        const footerHeight = footer ? footer.clientHeight : 0

        // Find card body padding
        const styles = getComputedStyle(parentCardBody)
        const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)

        // Calculate the max height of the chart container
        // [height of card] - [toolbar height] - [footer height] - [padding] = [chart viewport]
        const viewport = cardHeight - toolbarHeight - footerHeight - padding

        // Chart.js sizes the canvas to the container's max-height (it reads max-height but
        // ignores min-height — see getContainerSize in chart.js). A horizontal "ranked
        // breakdown" chart intentionally grows past the viewport (#labelDrivenHeight,
        // ~25px/label, card body scrolls), so the cap has to rise to meet that taller
        // content. Capping at the viewport would clip the canvas and squash the bars even
        // though min-height already made the container box tall.
        const maxHeight = Math.max(viewport, this.#labelDrivenHeight())
        const next = `${maxHeight}px`
        // Avoid re-writing the same value: the htmlLegend plugin calls this from
        // afterUpdate, and a needless style change can re-trigger a resize loop.
        if (this.chartContainerTarget.style.maxHeight !== next) {
            this.chartContainerTarget.style.maxHeight = next
        }
    }

    // ---- Header controls (combo renderer) -------------------------------------
    // Wired from chart_configurations/header_controls/<type> via data-action. The controls
    // live in the .card-header and this controller owns the .card, so it reaches them with
    // plain queries — no cross-controller plumbing.

    setComboMode(event) {
        const mode = event.params.mode
        if (this.comboState.mode === mode) return
        this.comboState.mode = mode
        this.#buildComboChart()
    }

    toggleReceivables(event) {
        this.comboState.showReceivables = event.target.checked
        this.#buildComboChart()
    }

    // Hour × Day Density heatmap: swap the Volume / Receivables % measure. Unlike the combo,
    // this MUTATES the live matrix chart (see hour_by_day_density.js) instead of rebuilding —
    // a destroyed+recreated matrix paints blank. State is not persisted; it lives on the chart.
    setDensityMeasure(event) {
        const measure = event.params.measure
        if (!this.chart || typeof this.chart.setDensityMeasure !== 'function') return
        this.densityMeasure = measure
        this.element
            .querySelectorAll('.js-density-measure')
            .forEach((btn) => btn.classList.toggle('active', btn.dataset.measure === measure))
        this.chart.setDensityMeasure(measure)
    }

    // Build (or rebuild) a custom-renderer chart (e.g. the calls combo) with the current
    // theme + toggle state, then sync the header controls. Returns false for charts without
    // a registered renderer, so callers fall through to the default Chart.js path. The combo
    // legend renders through the SAME htmlLegend plugin every chart uses (passed in here).
    #buildComboChart() {
        const renderer = CUSTOM_RENDERERS[this.optionsFromParam.renderer]
        if (!renderer) return false

        this.#destroyChart()
        this.chart = renderer(this.canvasTarget, this.optionsFromParam, {
            ...this.comboState,
            measure: this.densityMeasure,
            plugins: [this.htmlLegendPlugin],
        })
        this.#syncComboControls()
        this.resizeChart()
        return true
    }

    // Reflect comboState into the header markup: the active mode button and the Receivables %
    // switch. No-ops when these targets are absent (every non-combo chart). The granularity
    // caption now renders in the footer legend (presenter `legendRightLabel`), and chip
    // hidden-state is owned by htmlLegend, so this never touches the footer legend.
    #syncComboControls() {
        this.comboModeButtonTargets.forEach((btn) =>
            btn.classList.toggle('active', btn.dataset.mode === this.comboState.mode),
        )

        if (this.hasComboSwitchTarget) {
            this.comboSwitchTarget.checked = this.comboState.showReceivables
        }
    }

    // Horizontal charts ("ranked breakdown" bars) might have a ton of labels, so expand the
    // height of the chart to ~25px per label. Otherwise it fills the container height.
    #setChartContainerHeightByLabelHeight() {
        const height = this.#labelDrivenHeight()
        if (height) {
            this.chartContainerTarget.style.minHeight = `${height}px`
        }
    }

    // The on-canvas height a horizontal "ranked breakdown" chart needs: ~25px per label so
    // each row stays legible. Returns 0 for vertical charts, or when few enough labels fit
    // the card body (<= 24rem) and the chart should just fill it; above that the body scrolls.
    // Shared by #setChartContainerHeightByLabelHeight (min-height) and resizeChart (max-height)
    // so the two never disagree — Chart.js clips the canvas to max-height, so a cap below this
    // would squash the bars.
    #labelDrivenHeight() {
        // resizeChart runs from the htmlLegend afterUpdate hook, which fires during
        // `new Chart(...)` before this.chart is assigned — fall back to the param options.
        const options = this.chart?.options ?? this.optionsFromParam.options ?? {}
        const labels = this.chart?.data?.labels ?? this.optionsFromParam.data.labels ?? []
        if (options.indexAxis !== 'y') return 0
        const height = labels.length * 25 // 25px is roughly the height of a chartjs label
        const fitsContainer = 387 // If label sizes fit within 24rem it fills the full container height
        return height > fitsContainer ? height : 0
    }

    // Ranked breakdown charts with long per-row totals (e.g. duplicate RTBs by source)
    // need a wider canvas than a narrow grid card; let the card body scroll horizontally.
    #applyRankedBreakdownHorizontalMinWidth() {
        if (!this.hasChartContainerTarget || !this.optionsFromParam.shouldShowTotals) return

        const totalsLabels =
            this.chart?.data?.totalsLabels ?? this.optionsFromParam.data?.totalsLabels
        if (!totalsLabels?.length) {
            this.chartContainerTarget.style.removeProperty('min-width')
            return
        }

        const longest = totalsLabels.reduce((max, label) => Math.max(max, String(label).length), 0)
        const minWidth = Math.ceil(160 + longest * 6.5 + 24)
        this.chartContainerTarget.style.minWidth = `${minWidth}px`
        this.chart?.resize()
    }

    get isEmpty() {
        // if there is no dataset -> true
        // if there is no data in all datasets -> true
        // if there are only 0 in all data in all datasets -> true
        if (this.optionsFromParam.type != 'choropleth') {
            return (
                !this.optionsFromParam.data.datasets ||
                this.optionsFromParam.data.datasets.every(
                    (dataset) => !dataset.data || dataset.data.every((x) => x == 0),
                )
            )
        }
        return false
    }

    get emptyData() {
        const palette = chartPalette()
        const emptyData = {
            labels: ['No Data'],
            datasets: [
                {
                    label: 'No Data',
                    // For pie and doughnut we want a round circle and that is why the data is [1]
                    data: ['pie', 'doughnut'].includes(this.optionsFromParam.type) ? [1] : [],
                    backgroundColor: [palette.emptyFill], // Neutral color for empty state
                    borderColor: [palette.emptyBorder], // Border for empty state
                    borderWidth: 1,
                },
            ],
        }

        return emptyData
    }

    get optionsFromParam() {
        return JSON.parse(this.optionsTarget.textContent)
    }
    get enabledPlugins() {
        return JSON.parse(this.optionsTarget.textContent).plugins || []
    }
    get shouldSortData() {
        return this.optionsFromParam.shouldSortData || false
    }

    get defaultRetreaverChartOptions() {
        const palette = chartPalette()
        const options = {
            maintainAspectRatio: false,
            responsive: true,
            resizeDelay: 50,
            scales: {
                x: {
                    grid: {
                        color: palette.grid,
                        lineWidth: 1,
                    },
                },
                y: {
                    grid: {
                        color: palette.grid,
                        lineWidth: 1,
                    },
                },
            },
            animation: {
                onComplete: (chart) => {
                    if (!this.chartRendered) {
                        // Broadcast rendered event, useful for cross-controller coordination
                        this.dispatch('rendered', { detail: { chart } })
                        this.chartRendered = true
                    }
                },
            },
            plugins: {
                // The canvas legend is disabled; the htmlLegend plugin renders the
                // legend into the card footer instead (see #renderHtmlLegend). We keep
                // the options object so generateLabels still resolves for the HTML legend.
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        beforeLabel: (tooltipItem) => {
                            // data can come in three different ways raw.value, raw.x, raw
                            // The way what we need to provide this in order to work is:
                            // datasets: [ { label: "...", data: ...., formatter: 'dollar'} ]

                            const { formatter } = tooltipItem.dataset
                            const { raw } = tooltipItem
                            const selectedValue =
                                raw && typeof raw === 'object'
                                    ? (raw.value ?? raw.x ?? raw.y ?? raw)
                                    : raw
                            const numericValue =
                                typeof selectedValue === 'number'
                                    ? selectedValue
                                    : Number(selectedValue)

                            tooltipItem.formattedValue = formatNumber(numericValue, formatter)
                        },
                    },
                },
            },
            pointRadius: 0,
            pointHoverRadius: 0,
            cubicInterpolationMode: 'monotone',
            tension: 0.4,
        }

        if (this.optionsFromParam.type == 'line') {
            // Show every series' value at the hovered x-position (one tooltip listing all
            // datasets), regardless of how close the cursor is to a point. This is Chart.js'
            // native equivalent of the multi-series tooltip the crosshair plugin used to give us.
            options.interaction = { mode: 'index', intersect: false }
        }

        if (['choropleth'].includes(this.optionsFromParam.type)) {
            options.plugins.legend.display = false
            options.plugins.tooltip.callbacks.title = (context) =>
                // NOTE: The tooltip title should be the state name -> Texas.
                context[0]?.raw?.feature?.properties?.name || ''

            options.scales = {
                projection: {
                    axis: 'x',
                    projection: 'albersUsa',
                },
                color: {
                    axis: 'x',
                    // Themed cyan ramp; zero/low fades to transparent so the card background
                    // (which flips with color mode) shows through — see choroplethInterpolate.
                    interpolate: choroplethInterpolate(palette),
                    // No-data states (value: nil) stay transparent rather than a hard white,
                    // so they read as the themed background like every other empty state.
                    missing: 'transparent',
                    // The color scale legend is rendered as a horizontal gradient in the
                    // card footer (#renderColorScaleLegend) instead of on the canvas.
                    display: false,
                },
            }

            // chartjs-chart-geo only drives the fill (backgroundColor) off the color scale;
            // the feature border otherwise falls through to the faint global grid color, so
            // empty states nearly vanish. Give the outline a dedicated themed color/width so
            // the map's shape reads even where the fill is transparent (matches the mockup).
            options.elements = {
                geoFeature: {
                    borderColor: palette.choroplethBorder,
                    borderWidth: 0.5,
                },
            }
        }

        // Round a cap on the growing end of every bar — the shared geometry (and the stacked
        // last-visible-segment logic) lives in palette.js so all bar charts round identically.
        // borderSkipped defaults to 'start' (the bar's base edge) and Chart.js zeroes any radius
        // corner touching the skipped edge, which would silently strip the left/bottom caps.
        if (this.optionsFromParam.type === 'bar') {
            const horizontal = this.optionsFromParam.options?.indexAxis === 'y'
            const valueAxis = horizontal ? 'x' : 'y'
            const stacked = !!this.optionsFromParam.options?.scales?.[valueAxis]?.stacked
            options.elements = {
                bar: {
                    borderRadius: barCapBorderRadius(horizontal, stacked),
                    borderSkipped: false,
                },
            }
        }

        if (this.enabledPlugins.includes('datalabels')) {
            options.layout = { padding: { right: 40 } }

            options.plugins.datalabels = {
                padding: 0,
                font: { weight: 600, size: 11 },

                // put it at the very end of the stacked row
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                clip: false,
                display: (ctx) => {
                    // only on the last *visible* dataset of this stack
                    const { chart, dataset } = ctx
                    let lastVisible = 1
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) lastVisible = i
                    })
                    return ctx.datasetIndex === lastVisible
                },
                formatter: (value, ctx) => {
                    // sum only visible datasets in this stack for this row
                    const { chart, dataset, dataIndex } = ctx

                    let sum = 0
                    chart.data.datasets.forEach((ds, i) => {
                        if (chart.isDatasetVisible(i) && ds.stack === dataset.stack) {
                            const v = ds.data[dataIndex] ?? 0
                            sum += typeof v === 'object' ? (v.x ?? v.y ?? 0) : v
                        }
                    })

                    // We need the same formatter for all datasets in order to format the summed datalabel
                    let formatter
                    const formatters = [...new Set(chart.data.datasets.map((ds) => ds.formatter))]
                    if (formatters.length > 1) {
                        console.error(
                            'There are more than one formatter and cannot apply format: ',
                            formatters,
                        )
                    } else {
                        formatter = formatters[0]
                    }

                    return formatNumber(sum, formatter)
                },
            }

            if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
                // # Easier to override the above config
                options.plugins.datalabels.anchor = null
                options.plugins.datalabels.color = 'white'
                options.plugins.datalabels.display = 'auto'
                options.plugins.datalabels.backgroundColor = (context) =>
                    context.dataset.backgroundColor
            }
        }

        if (['pie', 'doughnut'].includes(this.optionsFromParam.type)) {
            options.plugins.tooltip = {
                // This is for showing how many % is the piece of the pie.
                callbacks: {
                    label(context) {
                        // Some slices might be toggled off via the HTML legend, so the
                        // percentage is computed against only the visible slices. We read
                        // visibility from chart.getDataVisibility — the canvas legend is
                        // disabled (display:false) so chart.legend can't be relied on.
                        const { chart } = context
                        let total = 0
                        context.dataset.data.forEach((value, index) => {
                            if (chart.getDataVisibility(index)) {
                                total += value
                            }
                        })

                        // Fallback if every slice is hidden, to avoid dividing by zero.
                        if (total === 0) {
                            total = context.dataset.data.reduce((sum, value) => sum + value, 0)
                        }

                        const value = context.raw
                        const percentage = ((value / total) * 100).toFixed(2)
                        const { tooltipValues } = context.chart.config.data
                        const tooltipValue =
                            (tooltipValues && tooltipValues[context.dataIndex]) ?? value
                        return `${tooltipValue} (${percentage}%)`
                    },
                },
            }
        }

        return options
    }

    // Inline Chart.js plugin that renders the legend into the card footer as HTML,
    // mirroring the official htmlLegend sample. Scoped per controller instance so the
    // many charts on a dashboard each target their own footer (this.legendTarget)
    // rather than a shared global element id.
    get htmlLegendPlugin() {
        return {
            id: 'htmlLegend',
            // afterUpdate fires on every chart update (initial render, refresh, toggle).
            afterUpdate: (chart) => this.#renderHtmlLegend(chart),
        }
    }

    #renderHtmlLegend(chart) {
        if (!this.hasLegendTarget) return
        // afterUpdate can fire while the `options` <script> (lazy-loaded in the inner
        // turbo frame) is mid-swap and momentarily detached. isEmpty/optionsFromParam
        // read from it, so bail rather than throw "Missing target element options".
        if (!this.hasOptionsTarget) return

        const container = this.legendTarget

        // Continuous color-scale charts (choropleth maps, the density heatmap) render a
        // horizontal gradient legend instead of discrete chips. Both reduce to one model and
        // one renderer; a null model (no data / a single flat value) just hides the footer.
        if (this.#hasColorScaleLegend(chart)) {
            const model = this.#colorScaleLegendModel(chart)
            if (model) {
                this.#renderColorScaleLegend(container, model)
            } else {
                container.classList.add('d-none')
                container.replaceChildren()
            }
            this.resizeChart()
            return
        }

        if (this.isEmpty) {
            // No legend for the empty "No Data" placeholder.
            container.classList.add('d-none')
            container.replaceChildren()
        } else {
            // Reuse Chart.js' built-in label generator — it still resolves even though
            // the canvas legend is disabled.
            const items = chart.options.plugins.legend.labels.generateLabels(chart)

            let list = container.querySelector('ul')
            if (!list) {
                list = document.createElement('ul')
                list.className =
                    'chart-legend list-unstyled d-flex flex-wrap m-0 row-gap-1 column-gap-3'
                container.appendChild(list)
            }
            list.replaceChildren()

            items.forEach((item) => {
                const li = document.createElement('li')
                li.className = 'chart-legend-item d-flex align-items-center gap-1 small lh-sm'
                // is-hidden is the test/state hook; opacity-50 dims the toggled-off chip.
                if (item.hidden) li.classList.add('is-hidden', 'opacity-50')
                li.addEventListener('click', () => this.#onLegendItemClick(item))

                // Swatch: line series read as a stroke (dashed when the dataset is) with a
                // centre dot; bar/pie series read as a filled box. The dataset's type drives
                // the choice, so this works for any chart — including mixed bar+line combos.
                const dataset =
                    item.datasetIndex != null ? chart.data.datasets[item.datasetIndex] : null
                const isLine = (dataset?.type || chart.config.type) === 'line'

                const swatch = document.createElement('span')
                swatch.className = 'chart-legend-swatch flex-shrink-0'
                if (isLine) {
                    swatch.classList.add('chart-legend-swatch-line')
                    if (Array.isArray(dataset?.borderDash) && dataset.borderDash.length) {
                        swatch.classList.add('is-dashed')
                    }
                    // The line/dot are CSS pseudo-elements coloured via this custom property.
                    swatch.style.setProperty('--chart-legend-color', item.strokeStyle)
                } else {
                    swatch.classList.add('rounded-1')
                    // Bars/pies: fillStyle can be a CanvasGradient (not a CSS string) — fall back
                    // to the series' solid stroke color so the swatch stays filled.
                    swatch.style.background =
                        typeof item.fillStyle === 'string' ? item.fillStyle : item.strokeStyle
                    swatch.style.borderColor = item.strokeStyle
                }
                li.appendChild(swatch)

                // Long names ellipsize via CSS (max-width in charts.scss); the title
                // attribute keeps the full text reachable on hover.
                const label = document.createElement('span')
                label.className = 'chart-legend-label text-truncate'
                if (item.hidden) label.classList.add('text-decoration-line-through')
                label.textContent = item.text
                label.title = item.text
                li.appendChild(label)

                list.appendChild(li)
            })

            const right = this.#buildLegendRightLabel(this.optionsFromParam.legendRightLabel)
            if (right) list.appendChild(right)
            container.classList.remove('d-none')
        }

        // The footer's presence/height changes the room left for the canvas.
        this.resizeChart()
    }

    // The far-right label both legend variants share: ms-auto pushes it to the opposite end of
    // the row. The discrete chip legend feeds it the presenter's `legendRightLabel` (a plain
    // string, e.g. the combo's granularity caption); the color-scale legend feeds it a rich
    // `{ html, color }` caption (e.g. the heatmap's colored peak). Returns null when empty.
    // `tag` is 'li' inside the chip <ul>, 'span' inside the color-scale row.
    #buildLegendRightLabel(label, tag = 'li') {
        if (!label) return null
        const el = document.createElement(tag)
        el.className = 'chart-legend-right ms-auto text-nowrap ps-3'
        if (typeof label === 'string') {
            el.textContent = label
        } else {
            if (label.color) el.style.color = label.color
            el.innerHTML = label.html
        }
        return el
    }

    // A chart shows a continuous color-scale legend (a gradient rail) rather than discrete
    // series chips when it's a choropleth map OR any custom renderer that attaches a model
    // builder at options.plugins.htmlLegend.colorScale (e.g. the density heatmap). We read the
    // RAW config (chart.config.options) — chart.options is the resolved proxy, which would
    // invoke a function-valued option on read instead of handing back the function.
    #hasColorScaleLegend(chart) {
        return (
            chart.config.type === 'choropleth' ||
            typeof chart.config.options?.plugins?.htmlLegend?.colorScale === 'function'
        )
    }

    // Normalize a color-scale chart down to one model the renderer below understands:
    //   { stops: [css color…], minText, maxText, startText?, end?: { html, color } }
    // Two sources, one shape — so the choropleth and the heatmap share #renderColorScaleLegend.
    // Returns null when there's nothing meaningful to show (no data / a single flat value).
    //
    //   • Custom renderers pass a builder via options.plugins.htmlLegend.colorScale; it's
    //     re-read on every chart.update() so a live toggle (e.g. the heatmap's measure switch)
    //     repaints the footer with no bespoke wiring.
    //   • Choropleth maps read straight off the chartjs-chart-geo color scale, so the rail
    //     matches the map exactly.
    #colorScaleLegendModel(chart) {
        const builder = chart.config.options?.plugins?.htmlLegend?.colorScale
        if (typeof builder === 'function') return builder()

        const scale = chart.scales.color
        const min = scale && scale.min
        const max = scale && scale.max
        if (!scale || !Number.isFinite(min) || !Number.isFinite(max) || min === max) return null

        // Sample the scale across [0, 1] (normalized value space) into gradient stops.
        const steps = 16
        const stops = []
        for (let i = 0; i <= steps; i++) {
            stops.push(scale.getColor(i / steps))
        }
        const { formatter, label } = chart.data.datasets[0] || {}
        return {
            stops,
            minText: formatNumber(min, formatter),
            maxText: formatNumber(max, formatter),
            startText: label,
        }
    }

    // Paint a color-scale model as a horizontal gradient rail with min/max labels (mirroring,
    // in HTML, the on-canvas legends we disable). Purely presentational — the model owns the
    // colors and text. Optional slots: `startText` (a leading title, e.g. the choropleth's
    // dataset label) and `rightLabel` (the shared right-aligned caption, e.g. the heatmap's
    // peak — same treatment as the chip legend's legendRightLabel).
    #renderColorScaleLegend(container, model) {
        const wrap = document.createElement('div')
        wrap.className = 'chart-color-legend hstack gap-2 w-100 small'

        if (model.startText) {
            const title = document.createElement('span')
            title.className = 'chart-color-legend-title flex-shrink-0 fw-medium'
            title.textContent = model.startText
            wrap.appendChild(title)
        }

        const minLabel = document.createElement('span')
        minLabel.className =
            'chart-color-legend-label flex-shrink-0 text-body-secondary font-monospace'
        minLabel.textContent = model.minText
        wrap.appendChild(minLabel)

        const bar = document.createElement('span')
        bar.className = 'chart-color-legend-bar flex-fill rounded-1 border border-secondary-subtle'
        bar.style.background = `linear-gradient(to right, ${model.stops.join(', ')})`
        wrap.appendChild(bar)

        const maxLabel = document.createElement('span')
        maxLabel.className =
            'chart-color-legend-label flex-shrink-0 text-body-secondary font-monospace'
        maxLabel.textContent = model.maxText
        wrap.appendChild(maxLabel)

        const right = this.#buildLegendRightLabel(model.rightLabel, 'span')
        if (right) wrap.appendChild(right)

        container.replaceChildren(wrap)
        container.classList.remove('d-none')
    }

    #onLegendItemClick(item) {
        const { type } = this.chart.config

        if (['pie', 'doughnut'].includes(type)) {
            // Pie/doughnut have a single dataset; visibility is per slice.
            this.chart.toggleDataVisibility(item.index)
            this.chart.update()
            return
        }

        // bar/line: visibility is per dataset.
        this.chart.setDatasetVisibility(
            item.datasetIndex,
            !this.chart.isDatasetVisible(item.datasetIndex),
        )
        this.chart.update()

        // Ranked breakdown bars re-sort after a toggle. The 550ms delay is a stylistic
        // choice so you see the bar disappear and then the rows re-arrange.
        if (this.shouldSortData) {
            setTimeout(() => {
                this.#sortAndReorderData()
                this.chart.update()
            }, 550)
        }
    }

    #sortAndReorderData() {
        if (!this.shouldSortData) {
            return
        }

        // Computing the order of the indexes of all visible data
        const labelsLength = this.chart.data.labels.length
        const keys = new Array(labelsLength).fill(0)
        this.chart.data.datasets.forEach((_, idx) => {
            if (!this.chart.isDatasetVisible(idx)) {
                return
            }
            for (let i = 0; i < labelsLength; i++) {
                keys[i] += +this.optionsFromParam.data.datasets[idx].data[i] || 0
            }
        })
        const order = [...keys.keys()].sort((a, b) => keys[b] - keys[a])

        // Reordering labels
        this.chart.data.labels = order.map((i) => this.optionsFromParam.data.labels[i])

        // Reordering data inside each dataset
        this.chart.data.datasets.forEach((ds, idx) => {
            ds.data = order.map((i) => this.optionsFromParam.data.datasets[idx].data[i])
        })
    }
}
